/**
 * Whether a kiosk design fits a set of services: its operation profile's.
 *
 * A design fits when every service it has a button for is offered, and every
 * service offered has a button. A grid that chose nothing shows whatever is
 * offered and so leaves nothing without a button.
 *
 * Hiding a button for a service the branch cannot issue still happens at the
 * kiosk (lib/kioskPages.ts) — that is the net under a service taken out of the
 * profile after the design was drawn.
 */

import type { LayoutDocument } from '../layout/types'

type HasId = { id: string }

/** The services a design issues, and whether one of its grids issues everything. */
function issued(doc: LayoutDocument): { ids: string[]; everything: boolean } {
  const ids: string[] = []
  let everything = false
  for (const el of doc.elements) {
    if (el.kind === 'button' && el.action?.kind === 'service' && el.action.serviceId) ids.push(el.action.serviceId)
    if (el.kind === 'services') {
      const chosen = el.options?.serviceIds
      if (!chosen || chosen.length === 0) everything = true
      else ids.push(...chosen)
    }
  }
  return { ids: [...new Set(ids)], everything }
}

export interface Match {
  /** Services the design has a button for that are not offered. */
  missing: string[]
  /** Services offered that the design has no button for. */
  uncovered: string[]
}

export function matchBranch(doc: LayoutDocument, offered: readonly HasId[]): Match {
  const { ids, everything } = issued(doc)
  const here = new Set(offered.map((s) => s.id))
  const has = new Set(ids)
  return {
    missing: ids.filter((id) => !here.has(id)),
    uncovered: everything ? [] : offered.map((s) => s.id).filter((id) => !has.has(id)),
  }
}

export function matches(m: Match): boolean {
  return m.missing.length === 0 && m.uncovered.length === 0
}
